import { addHours, Locale } from 'date-fns';
import { DropdownOption } from 'src/common/components/DropdownDS22';
import {
  BASE_API_URI,
  BOOK_NOW_DEPARTURE_TIME_FORMAT,
  CAPTAIN_OPTION_1_INCLUDED_IN_PRICE,
  CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN,
  CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE,
} from 'src/common/constants';
import { timeStringToDateUpdate, tripTimeAsSeconds } from 'src/common/helpers';
import { InstabookAvailability } from 'src/common/types/api/v4/instabook';
import { CaptainOptionValue } from 'src/common/types/CaptainOptions';
import { BOOK_NOW_PICK_UP_TIME_FORMAT, formatDate } from 'src/common/utils/dateHelpers';
import { RecentInquiryData } from 'src/common/utils/recentInquiryCookieData';
import { MinimalRecentInquiry } from 'src/inquiry/types';
import { sortInstabookTripsByPrice } from 'src/listing/helper';
import type { Trip } from 'src/common/types/api/v4/trips';
import { apiRequest } from 'src/common/utils/apiFetch';
import ApiResponseError from 'src/common/utils/apiResponseError';
import getInstabookDepartureTimes from 'src/listing/utils/getInstabookDepartureTimes';
import type { BookNowFormStateSubmit } from './type';

/**
 * The id for the departure time would be composite of
 * tripId, departure time and renter trip amount. It will be
 * format as: {trip_id}-{departure_time}-{renter_amount}
 */
export const getDepartureTimeId = (trip: InstabookAvailability, dateTime: Date): string => {
  const {
    id,
    transaction: {
      amounts: {
        renter_booking_amount: renterAmount,
      },
    },
  } = trip;

  const formattedPickupTime = formatDate(dateTime, BOOK_NOW_PICK_UP_TIME_FORMAT);
  return `${id}-${formattedPickupTime}-${renterAmount}`;
};

export const getDepartureTimeOptions = (
  trip: InstabookAvailability,
  locale: Locale,
): DropdownOption<string>[] => {
  const options = getInstabookDepartureTimes(trip, locale)
    .map(({ dateTime, formattedTime12 }) => ({
      id: getDepartureTimeId(trip, dateTime),
      selectedLabel: formattedTime12,
      dropdownLabel: formattedTime12,
    }));

  return options;
};

/**
 *
 * @param departureTimeId should have format: {trip_id}-{departure_time}-{renter_amount}
 * @returns
 */
export const getInfoFromInstabookId = (departureTimeId: string): {
  tripId: string;
  departureTime: string;
  renterAmount: number | undefined;
} => {
  if (!departureTimeId) {
    return {
      tripId: '',
      departureTime: '',
      renterAmount: undefined,
    };
  }
  const v = departureTimeId.split('-');
  return {
    tripId: v[0],
    departureTime: v[1],
    renterAmount: Number(v[2]) || undefined,
  };
};

/**
 *
 * @param departureTimeId format: {trip_id}-{departure_time}-{renter_amount}
 * @returns either undefined if there is no departureTimeId or return
 * dropdown options.
 */
export const getSelectedTimeOption = (
  departureTimeId: string,
  locale: Locale,
): DropdownOption<string> | undefined => {
  if (!departureTimeId) {
    return {
      id: '',
      selectedLabel: '',
      dropdownLabel: '',
    };
  }

  const { departureTime } = getInfoFromInstabookId(departureTimeId);
  const date = timeStringToDateUpdate(departureTime);
  const formattedTime = formatDate(date, BOOK_NOW_DEPARTURE_TIME_FORMAT, locale);

  return {
    id: departureTimeId,
    selectedLabel: formattedTime,
    dropdownLabel: formattedTime,
  };
};

/**
 * This function returns formatted times for departure and
 * return times.
 * @param departureTimeId should have format: {trip_id}-{departure_time}-{renter_amount}
 */
export const getFormattedTimes = (departureTimeId: string, duration: number): {
  formattedDepartureTime: string;
  formattedReturnTime: string;
} => {
  const { departureTime } = getInfoFromInstabookId(departureTimeId);
  const formattedDepartureTime = formatDate(
    timeStringToDateUpdate(departureTime),
    BOOK_NOW_DEPARTURE_TIME_FORMAT,
  );

  const formattedReturnTime = formatDate(
    addHours(timeStringToDateUpdate(departureTime), duration),
    BOOK_NOW_DEPARTURE_TIME_FORMAT,
  );

  return {
    formattedDepartureTime,
    formattedReturnTime,
  };
};

export const getDepartureTimes = (
  instabookTrips: InstabookAvailability[],
  locale: Locale,
  captainOption?: CaptainOptionValue,
): DropdownOption[] => {
  let filteredTrips = instabookTrips;
  if (captainOption) {
    filteredTrips = captainOption === CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN
    || captainOption === CAPTAIN_OPTION_1_INCLUDED_IN_PRICE
      ? instabookTrips.filter((trip) => trip.captained)
      : instabookTrips.filter((trip) => !trip.captained);
  }

  const options = filteredTrips.flatMap((trip) => getDepartureTimeOptions(trip, locale));
  return options.sort((a: DropdownOption, b: DropdownOption) => {
    const { departureTime: aTime, renterAmount: aAmount } = getInfoFromInstabookId(a.id);
    const { departureTime: bTime, renterAmount: bAmount } = getInfoFromInstabookId(b.id);
    const maxAmount = Math.max(aAmount || 1, bAmount || 1);

    // reduce weight of price to fractional, trip time is primary sort order
    const weightA = tripTimeAsSeconds(aTime) + (aAmount || 0) / maxAmount;
    const weightB = tripTimeAsSeconds(bTime) + (bAmount || 0) / maxAmount;

    return weightA - weightB;
  });
};

export const getInitialInstabookTrip = (
  instabookTrips: InstabookAvailability[],
  tripCartCookieData: MinimalRecentInquiry | Partial<RecentInquiryData>,
): InstabookAvailability => {
  const captained = (
    tripCartCookieData.captainOptionValue !== CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE
  );
  const sorted = instabookTrips.slice().sort(sortInstabookTripsByPrice);
  const trip = sorted.find((instabook) => instabook.captained === captained);
  if (trip) {
    return trip;
  }
  return sorted[0];
};

/** Required to get trip.pk for addons and checkout */
export const patchInstabook = async (
  instabookId: InstabookAvailability['id'],
  values: BookNowFormStateSubmit,
): Promise<Trip> => {
  const response = await apiRequest(`${BASE_API_URI}instabook/trips/${instabookId}/book/`, {
    method: 'PATCH',
    body: JSON.stringify({
      adults: values.guests.adults,
      pickup_time: values.departureTime,
      inquiry_comment: values.ownerMessage ?? '',
      occasions: values.occasion ? [Number(values.occasion)] : undefined,
    }),
    bypass401Handling: true,
  });

  if (response.ok) {
    return response.json();
  }
  throw new ApiResponseError(response);
};
